import { router } from "..";
import { getSp2, putSp2 } from "../api/smartplug2s_{imei}";
import { prices } from "../billing/prices";
import { deviceCard, formatSubscriptionStatus } from "../device/device_card";
import { errorSection } from "../error-section";
import { loadingScreen } from "../loading-screen";
import { relativeTimeString, t } from "../locale";
import { addNotificationListener } from "../mobile";
import { state } from "../state";
import { keyValueParagraph } from "../view/components";
import { Html } from "../view/html";
import { MaterialDesign } from "../view/material_design";
import { getSmartplug2Warnings } from "./sp2_warnings";

/**
 * @param {import("./sp2").Smartplug2} sp2
 */
async function formatSubscriptionPlan(sp2) {
	if (sp2.Subscription.Trialing) return t.trial;

	const { Smartplug2Month, Smartplug2Year } = await prices;

	switch (sp2.Subscription.PriceIds.Current) {
		case Smartplug2Month.Id:
			return t.monthly;
		case Smartplug2Year.Id:
			return t.yearly;
	}

	const expiryTime = new Date(sp2.Subscription.ExpiresAt);
	if (Date.now() < expiryTime.getTime()) return "";

	return t.none;
}

/**
 * @param {import("./sp2").Smartplug2} sp2
 */
async function smartplug2Card(sp2) {
	addNotificationListener("smartplug2", sp2.Imei, () => {
		getSp2(sp2.Imei).then((sp2) => {
			state.smartplug2s[sp2.Imei] = sp2;
		});
	});

	const powerOnIcon = MaterialDesign.icon({
		id: "flash_on",
		onClick: onClickTogglePower,
	});

	const powerOffIcon = MaterialDesign.icon({
		id: "flash_off",
		onClick: onClickTogglePower,
	});

	let powerStatusString = "?";
	if (sp2.DeviceConfig.OutputPower && sp2.TargetConfig.OutputPower) {
		powerStatusString = t.on;
	}
	if (!sp2.DeviceConfig.OutputPower && !sp2.TargetConfig.OutputPower) {
		powerStatusString = t.off;
	}

	const card = deviceCard({
		deviceName: sp2.Metadata.Name,
		deviceType: "smartplug2",
		productName: "SmartPlug2",
		warnings: getSmartplug2Warnings(sp2),
		lines: [
			keyValueParagraph(t.device.card.statusLabel),
			keyValueParagraph(
				`- ${t.smartplug.card.powerLabel}`,
				powerStatusString,
			),
			keyValueParagraph(
				`- ${t.smartplug.powerLoadLabel}`,
				`${sp2.Load} W`,
			),
			keyValueParagraph(
				`- ${t.smartplug.consumptionLabel}`,
				`${sp2.Consumption} Wh`,
			),
			keyValueParagraph(
				`- ${t.signalStrength}`,
				`${sp2.SignalStrength}%`,
			),
			keyValueParagraph(
				`- ${t.lastActive}`,
				relativeTimeString(sp2.LastUploadAt),
			),
			keyValueParagraph(t.device.subscriptionLabel),
			keyValueParagraph(
				`- ${t.device.card.planLabel}`,
				await formatSubscriptionPlan(sp2),
			),
			keyValueParagraph(...formatSubscriptionStatus(sp2.Subscription)),
		],
		buttons: [
			Html.anchor(
				{},
				sp2.TargetConfig.OutputPower ? powerOnIcon : powerOffIcon,
			),
			Html.anchor(
				{},
				MaterialDesign.icon({
					id: "monitoring",
					onClick: () => router.jump(`/smartplug2/${sp2.Imei}/logs`),
				}),
			),
			Html.anchor(
				{},
				MaterialDesign.icon({
					id: "settings",
					onClick: () => router.jump(`/smartplug2/${sp2.Imei}`),
				}),
			),
		],
	});

	function onClickTogglePower() {
		sp2.TargetConfig.OutputPower = !sp2.TargetConfig.OutputPower;
		const attempt = errorSection.attempt(async () => {
			const newSp2 = await putSp2(sp2);
			state.smartplug2s[sp2.Imei] = newSp2;
			if (sp2.TargetConfig.OutputPower) {
				powerOffIcon.replaceWith(powerOnIcon);
			} else {
				powerOnIcon.replaceWith(powerOffIcon);
			}
		});
		loadingScreen.waitFor(attempt);
	}

	return card;
}

export { smartplug2Card };
